import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import AppLayout from '../../../../components/layout/AppLayout';
import { useCourseApi } from '../../../../api/courses/courses';
import { TLesson } from '../../../../models/courses/lesson';
import AssociationLessonAdmin from '../../../../components/AssociationLesson/admin/AssociationLessonAdmin';
import AssociationLessonStudent from '../../../../components/AssociationLesson/student/AssociationLessonStudent';
import AssociationLessonTeacher from '../../../../components/AssociationLesson/teacher/AssociationLessonTeacher';
import type { AssociationConfig } from '../../../../components/AssociationLesson/types';
import { createEmptyAssociationConfig } from '../../../../components/AssociationLesson/types';

const LessonAssociation: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { getLessonDetailsById, saveLessonDetails } = useCourseApi();

  const stateLesson: TLesson | undefined = location.state?.lesson;
  const teacherMode: boolean = location.state?.teacherMode || false;
  const epic = location.state?.epic;

  const [editingLesson, setEditingLesson] = useState<TLesson | null>(stateLesson ?? null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [importedStatement, setImportedStatement] = useState<string>('');

  const lessonIdFromUrl = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const lessonId = params.get('lessonId');
    return lessonId ? parseInt(lessonId, 10) : undefined;
  }, [location.search]);

  useEffect(() => {
    const lessonId = lessonIdFromUrl ?? stateLesson?.id;
    if (!lessonId) {
      message.warning(t('associationLesson.page.messages.selectLesson') || 'Choisissez une leçon');
      navigate('/setup/lesson', epic ? { state: { epic } } : undefined);
      return;
    }
    setLoading(true);
    getLessonDetailsById(lessonId)
      .then((response) => {
        const lessonData = response.data;
        const existingConfig = lessonData.config?.config as AssociationConfig | undefined;
        lessonData.config = {
          id: lessonData.config?.id ?? 0,
          lesson_id: lessonData.id,
          config: existingConfig?.left ? existingConfig : createEmptyAssociationConfig(),
        };
        setEditingLesson(lessonData);
        setImportedStatement(lessonData.statement || '');
      })
      .catch((error) => {
        message.error(error.response?.data ?? error.message);
        navigate('/setup/lesson', epic ? { state: { epic } } : undefined);
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const currentConfig: AssociationConfig = useMemo(() => {
    const rawConfig = editingLesson?.config?.config as AssociationConfig | undefined;
    if (rawConfig?.left && rawConfig?.right) {
      return { ...createEmptyAssociationConfig(), ...rawConfig };
    }
    return createEmptyAssociationConfig();
  }, [editingLesson]);

  const handleConfigChange = (config: AssociationConfig) => {
    setEditingLesson((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        config: {
          id: prev.config?.id ?? 0,
          lesson_id: prev.id,
          config,
        },
      };
    });
  };

  const handleSave = async () => {
    if (!editingLesson) return;
    setSaving(true);
    try {
      const payload: TLesson = {
        ...editingLesson,
        statement: importedStatement || editingLesson.statement || (currentConfig?.statement ?? ''),
        config: {
          id: editingLesson.config?.id ?? 0,
          lesson_id: editingLesson.id,
          config: currentConfig,
        },
      };
      await saveLessonDetails(payload);
      message.success(t('associationLesson.page.messages.saveSuccess') || 'Configuration sauvegardée');
    } catch (error: any) {
      message.error(error.response?.data ?? error.message ?? error);
    } finally {
      setSaving(false);
    }
  };

  const handleBackNavigation = () => {
    navigate(-1);
  };

  return (
    <AppLayout
      title={editingLesson?.name || t('associationLesson.page.title') || "Leçon d'association"}
      onBackNavigation={handleBackNavigation}
      showBackButton
      loading={loading}
    >
      {editingLesson && (
        teacherMode ? (
          <AssociationLessonTeacher
            config={currentConfig}
            statement={editingLesson.statement || currentConfig.statement}
          />
        ) : (
          <AssociationLessonAdmin
            config={currentConfig}
            onConfigChange={handleConfigChange}
            onSave={handleSave}
            saving={saving}
            initialStatement={importedStatement}
            onStatementChange={setImportedStatement}
          />
        )
      )}
      {!editingLesson && teacherMode && (
        <AssociationLessonTeacher config={createEmptyAssociationConfig()} />
      )}
      {!editingLesson && !teacherMode && (
        <AssociationLessonStudent config={createEmptyAssociationConfig()} />
      )}
    </AppLayout>
  );
};

export default LessonAssociation;
